// frontend/src/routing.js
//
// Locale-aware routing.
//
// Every page used to live at exactly one URL regardless of language: /browse
// served Greek or English depending on a localStorage key. That is invisible to
// a search engine — Google fetches a URL once, gets one language, and indexes
// one language. The English half of the site could not be indexed at all, and
// nothing on the page told Google that a Greek and an English version of it
// existed.
//
// The fix is one URL per language per page — /el/browse and /en/browse — with
// hreflang linking them (see components/Seo.js). This module is what keeps that
// true *while you navigate*: if internal links kept pointing at /browse, one
// click from /en/browse would drop the visitor back onto an unprefixed URL and
// the crawler would follow it there.
//
// So `Link` and `useNavigate` are re-exported here with the prefix applied, and
// the pages import them from this file instead of from react-router-dom. Call
// sites are unchanged — `<Link to="/browse">` still reads as /browse — which is
// the point: the prefix is a routing concern, not something 78 call sites
// should each have to remember.
import React from 'react';
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  Navigate as RouterNavigate,
  useNavigate as useRouterNavigate,
  useLocation,
} from 'react-router-dom';
import { LANGUAGES, DEFAULT_LANGUAGE, getLanguage, setLanguage } from './i18n';

export const LANGUAGE_KEYS = Object.keys(LANGUAGES);

/** True for the first path segment of a localized URL: 'el', 'en', … */
export const isLanguageSegment = (segment) =>
  Boolean(segment) && Object.prototype.hasOwnProperty.call(LANGUAGES, segment);

/**
 * Split '/en/product/123' into { lang: 'en', rest: '/product/123' }.
 * An unprefixed path returns { lang: null, rest: <path unchanged> }.
 */
export function splitLocale(pathname = '/') {
  const [, first, ...others] = String(pathname).split('/');
  if (!isLanguageSegment(first)) return { lang: null, rest: pathname || '/' };
  return { lang: first, rest: '/' + others.join('/') };
}

/** The path with any locale prefix removed — '/en/browse' → '/browse'. */
export const stripLocale = (pathname) => splitLocale(pathname).rest;

// Anything that is not an in-app path: absolute URLs, mailto:, tel:, and bare
// fragments. Prefixing these would corrupt them.
const isExternal = (to) =>
  typeof to === 'string' && (/^[a-z][a-z0-9+.-]*:/i.test(to) || to.startsWith('//') || to.startsWith('#'));

/**
 * Add (or correct) the language prefix on an in-app path.
 *
 * Idempotent by design: localizePath('/en/browse', 'el') is '/el/browse', not
 * '/el/en/browse'. Code that already built a prefixed path — a redirect target
 * read back out of the URL, say — therefore stays correct.
 */
export function localizePath(to, lang = getLanguage()) {
  const lng = isLanguageSegment(lang) ? lang : DEFAULT_LANGUAGE;

  if (to == null || isExternal(to)) return to;

  if (typeof to === 'object') {
    if (typeof to.pathname !== 'string') return to;
    return { ...to, pathname: localizePath(to.pathname, lng) };
  }

  // Relative paths ('../edit', 'edit') are resolved by the router against the
  // current match, which is already inside the locale. Leaving them alone is
  // what keeps that resolution correct.
  if (!to.startsWith('/')) return to;

  // Split off ?query and #hash before touching the path, so that
  // '/browse?color=blonde' prefixes the path and leaves the query intact.
  const cut = [to.indexOf('#'), to.indexOf('?')].filter((i) => i >= 0).sort((a, b) => a - b)[0];
  const pathname = cut === undefined ? to : to.slice(0, cut);
  const suffix = cut === undefined ? '' : to.slice(cut);

  const { rest } = splitLocale(pathname);
  const clean = rest === '/' ? '' : rest;
  return `/${lng}${clean}${suffix}`;
}

/** `<Link to="/browse">` → href="/el/browse". Otherwise react-router's Link. */
export const Link = React.forwardRef(function Link({ to, ...rest }, ref) {
  return <RouterLink ref={ref} to={localizePath(to)} {...rest} />;
});

export const NavLink = React.forwardRef(function NavLink({ to, ...rest }, ref) {
  return <RouterNavLink ref={ref} to={localizePath(to)} {...rest} />;
});

/**
 * useNavigate, with the prefix applied.
 *
 * navigate(-1) and navigate(1) are passed straight through: a delta is a
 * history operation, not a path, and prefixing it would be nonsense.
 */
export function useNavigate() {
  const navigate = useRouterNavigate();
  return React.useCallback(
    (to, options) => (typeof to === 'number' ? navigate(to) : navigate(localizePath(to), options)),
    [navigate]
  );
}

/**
 * Switch language and stay on the page you are reading.
 *
 * The language toggle used to call setLanguage() alone, which swapped the text
 * under a URL that still said /en. With one URL per language that would be a
 * lie to both the reader and the crawler, so the toggle now navigates.
 *
 * `replace` keeps the back button meaning "the page before this one" rather
 * than "the same page in the other language".
 */
export function useSwitchLanguage() {
  const navigate = useRouterNavigate();
  const location = useLocation();
  return React.useCallback(
    async (lng) => {
      await setLanguage(lng);
      navigate(
        { pathname: localizePath(stripLocale(location.pathname), lng), search: location.search, hash: location.hash },
        { replace: true }
      );
    },
    [navigate, location.pathname, location.search, location.hash]
  );
}

/** `<Navigate to="/login">` → /el/login. Used by the route guards. */
export function Navigate({ to, ...rest }) {
  return <RouterNavigate to={localizePath(to)} {...rest} />;
}

// Re-exported so a page needs one import for routing rather than two, and so
// that `from 'react-router-dom'` appearing in a page is a reliable signal that
// something is bypassing the locale.
export { useLocation, useParams, useSearchParams, Outlet, useMatch } from 'react-router-dom';
